// Timezone-aware day boundaries for report filters.
//
// The date picker yields calendar days as the user sees them (browser-local
// midnight). Reports must filter on the *place* timezone, so we emit the
// start/end of the selected calendar day expressed in the place timezone, as
// proper ISO 8601 instants. The backend then just converts the instant to its
// canonical storage timezone (no time rounding needed).

const DEFAULT_TIMEZONE = "America/Sao_Paulo";

// Offset (ms) of `timeZone` relative to UTC at the given absolute instant.
// Computed at second resolution: `utcMs` is expected to be ms-aligned (0 ms),
// and the formatted parts carry no fractional seconds, so the result is a
// clean whole-minute offset without sub-second contamination.
function tzOffsetMs(utcMs, timeZone) {
    const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    });
    const parts = {};
    dtf.formatToParts(new Date(utcMs)).forEach((p) => {
        if (p.type !== "literal") parts[p.type] = parseInt(p.value, 10);
    });
    const asUTC = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour % 24,
        parts.minute,
        parts.second
    );
    return asUTC - utcMs;
}

// Convert a wall-clock (interpreted in `timeZone`) to an absolute instant and
// return its ISO string. Handles DST by re-checking the offset. Milliseconds
// are kept separate from the offset computation to avoid sub-second drift.
function zonedWallClockToISO(y, m, d, hh, mm, ss, ms, timeZone) {
    const wallNoMs = Date.UTC(y, m - 1, d, hh, mm, ss);
    let offset = tzOffsetMs(wallNoMs, timeZone);
    let utc = wallNoMs - offset;
    const offset2 = tzOffsetMs(utc, timeZone);
    if (offset2 !== offset) {
        utc = wallNoMs - offset2;
    }
    return new Date(utc + ms).toISOString();
}

function asDate(value) {
    return value instanceof Date ? value : new Date(value);
}

function canUseTz(timeZone) {
    return (
        timeZone &&
        typeof Intl !== "undefined" &&
        Intl.DateTimeFormat
    );
}

/**
 * Given an ISO instant (typically a place-tz day boundary produced by
 * placeStartOfDayISO/placeEndOfDayISO), return a browser-local Date at local
 * midnight for the calendar day that instant falls on *in the place timezone*.
 *
 * This is the inverse needed to seed the date picker from stored filter state:
 * the picker works in browser-local calendar days, so we must map the stored
 * instant back to the place-local day. Without this, reloading the page reads
 * the instant in the browser timezone and the day drifts when the two zones
 * straddle midnight (e.g. end-of-day creeping +1 day on every refresh).
 */
export function isoToPlaceLocalDate(value, timeZone = DEFAULT_TIMEZONE) {
    const d = asDate(value);
    if (!canUseTz(timeZone)) {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
    const parts = {};
    dtf.formatToParts(d).forEach((p) => {
        if (p.type !== "literal") parts[p.type] = parseInt(p.value, 10);
    });
    return new Date(parts.year, parts.month - 1, parts.day);
}

/**
 * Format an absolute instant (Date / ISO / RFC2822 string) as a readable
 * date-time on the place's local clock. Used for CSV exports and other plain
 * string contexts where the timezone-aware <human-date> component isn't used.
 */
export function formatInstantInPlaceTz(value, timeZone = DEFAULT_TIMEZONE, locale) {
    if (!value) return "";
    const d = asDate(value);
    if (isNaN(d.getTime())) return String(value);
    if (!canUseTz(timeZone)) {
        return d.toLocaleString(locale || undefined);
    }
    return new Intl.DateTimeFormat(locale || undefined, {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    }).format(d);
}

/**
 * The current calendar day ("YYYY-MM-DD") as it reads on the place's clock.
 * Use for place-local "today" comparisons (e.g. overdue / due-today flags)
 * instead of the browser's local day, which drifts when the two zones straddle
 * midnight.
 */
export function placeTodayDateString(timeZone = DEFAULT_TIMEZONE) {
    const now = new Date();
    if (!canUseTz(timeZone)) {
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, "0");
        const d = String(now.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }
    // en-CA renders as YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(now);
}

/**
 * ISO instant for 00:00:00.000 of the given calendar day, in the place tz.
 */
export function placeStartOfDayISO(date, timeZone = DEFAULT_TIMEZONE) {
    const d = asDate(date);
    if (!canUseTz(timeZone)) {
        const x = new Date(d);
        x.setHours(0, 0, 0, 0);
        return x.toISOString();
    }
    return zonedWallClockToISO(
        d.getFullYear(),
        d.getMonth() + 1,
        d.getDate(),
        0,
        0,
        0,
        0,
        timeZone
    );
}

/**
 * ISO instant for 23:59:59.999 of the given calendar day, in the place tz.
 */
export function placeEndOfDayISO(date, timeZone = DEFAULT_TIMEZONE) {
    const d = asDate(date);
    if (!canUseTz(timeZone)) {
        const x = new Date(d);
        x.setHours(23, 59, 59, 999);
        return x.toISOString();
    }
    return zonedWallClockToISO(
        d.getFullYear(),
        d.getMonth() + 1,
        d.getDate(),
        23,
        59,
        59,
        999,
        timeZone
    );
}
